import { Fragment, useMemo, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { FaChevronDown, FaCheck, FaTimes } from "react-icons/fa";

// Searchable, accessible combobox over a list of objects.
// - options: array of items
// - value: the currently-selected key (e.g. idFirebase)
// - onChange: called with the selected key (or "" when cleared)
// - getLabel: (item) => string shown in the row
// - getKey: (item) => the value passed to onChange
// - placeholder: input placeholder when nothing is selected
// - clearable: show an X button to reset selection
function SearchableSelect({
  options,
  value,
  onChange,
  getLabel = (o) => o.nombre,
  getKey = (o) => o.idFirebase,
  placeholder = "Buscar...",
  clearable = false,
  required = false,
}) {
  const [query, setQuery] = useState("");

  const sorted = useMemo(
    () => [...options].sort((a, b) => getLabel(a).localeCompare(getLabel(b), "es", { sensitivity: "base" })),
    [options, getLabel]
  );

  const filtered = useMemo(() => {
    if (!query) return sorted;
    const q = query.toLowerCase();
    return sorted.filter((o) => getLabel(o).toLowerCase().includes(q));
  }, [sorted, query, getLabel]);

  const selectedItem = useMemo(
    () => options.find((o) => getKey(o) === value) || null,
    [options, value, getKey]
  );

  return (
    <Combobox
      value={selectedItem}
      onChange={(item) => onChange(item ? getKey(item) : "")}
      nullable
    >
      <div className="relative">
        <div className="relative flex items-center w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent transition-shadow">
          <Combobox.Input
            className="flex-1 bg-transparent text-sm px-3 py-2 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none rounded-lg min-w-0"
            displayValue={(item) => (item ? getLabel(item) : "")}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            required={required && !selectedItem}
            title={selectedItem ? getLabel(selectedItem) : ""}
          />
          {clearable && selectedItem && (
            <button
              type="button"
              onClick={() => { onChange(""); setQuery(""); }}
              className="p-1 mr-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              tabIndex={-1}
            >
              <FaTimes className="w-3 h-3" />
            </button>
          )}
          <Combobox.Button className="px-2 py-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <FaChevronDown className="w-3 h-3" />
          </Combobox.Button>
        </div>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery("")}
        >
          <Combobox.Options className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg bg-white dark:bg-gray-800 py-1 text-sm shadow-lg ring-1 ring-black/5 dark:ring-white/10 focus:outline-none">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-gray-400">Sin resultados</div>
            ) : (
              filtered.map((item) => (
                <Combobox.Option
                  key={getKey(item)}
                  value={item}
                  title={getLabel(item)}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 pl-8 pr-3 ${
                      active
                        ? "bg-primary/10 dark:bg-primary/20 text-gray-900 dark:text-gray-100"
                        : "text-gray-700 dark:text-gray-200"
                    }`
                  }
                >
                  {({ selected, active }) => (
                    <>
                      {selected && (
                        <span className={`absolute left-2 top-2 ${active ? "text-primary" : "text-primary"}`}>
                          <FaCheck className="w-3 h-3" />
                        </span>
                      )}
                      <span className={`block break-words ${selected ? "font-medium" : ""}`}>
                        {getLabel(item)}
                      </span>
                    </>
                  )}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
}

export default SearchableSelect;
